import { gql, useMutation, useQuery, useApolloClient, NetworkStatus } from '@apollo/client'
import { isEmpty, isNumber } from 'lodash'
import { useEffect } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { useParams, useNavigate } from 'react-router-dom'

import Heading from 'components/v2/Heading'
import Card from '@10xLabs/react-web/components/Card'
import EmptyState from 'components/v2/EmptyState'
import { Loader } from '@10xLabs/react-web/components/Loader'
import { Button } from 'components/v2/Button'
import { Content, ContentHeader } from 'components/v2/Content'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { NewSegment } from 'features/Segments/components/NewSegment'
import { resolvers as loginResolvers } from 'resolvers/login'
import { getDifference } from 'utils/object'
import { resolvers } from 'resolvers/segments'
import { canWrite } from 'features/Users/utils'
import { useLogin } from 'features/Login/hooks'
import { GTSegmentCompanyLineSpec } from 'gql/types.generated'
import { round } from 'utils/math'

const messages = defineMessages({
  helmet: {
    id: 'segments.edit.helmet',
    defaultMessage: 'Edit Segment | Nexbus Admin'
  }
})

const EDIT_SEGMENT = gql`
  mutation EditSegment($segmentId: ID!, $companyLineSpecs: [companyLineSpecInput!]!) {
    updateSegment(input: { id: $segmentId, companyLineSpecs: $companyLineSpecs })
  }
`

const GET_SEGMENT = gql`
  query GetSegment($segmentId: ID!) {
    segment(input: { ID: $segmentId }) {
      id
      origin {
        id
        name
        code
      }
      destination {
        id
        name
        code
      }
      companyLineSpecs {
        companyLine {
          id
          name
        }
        duration
        distance
        realDistance
        fares {
          subtotal
        }
      }
      company {
        id
        name
      }
    }
  }
`

const EditSegmentView = (): TSFixMe => {
  let content
  const navigate = useNavigate()
  const { segmentId } = useParams()
  const intl = useIntl()
  const client = useApolloClient()
  client.addResolvers(loginResolvers)
  client.addResolvers(resolvers)

  const login = useLogin()
  const {
    loading: queryLoading,
    error: queryError,
    data: queryData,
    refetch,
    networkStatus
  } = useQuery(GET_SEGMENT, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: {
      segmentId
    }
  })
  const [editSegment, { data, loading, error }] = useMutation(EDIT_SEGMENT)

  useEffect((): TSFixMe => {
    document.title = intl.formatMessage(messages.helmet)
    refetch()
  }, [])

  useEffect((): TSFixMe => {
    if (data) {
      navigate('/segments')
    }
  }, [data])

  if (!canWrite(FEATURES.SEGMENT, login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.SEGMENT} />
  }

  const handleSubmit = async (values: TSFixMe): Promise<void> => {
    const oldCompanyLineSpecs = queryData?.segment?.companyLineSpecs.map(
      (lineSpec: GTSegmentCompanyLineSpec): TSFixMe => {
        return {
          companyLineID: lineSpec.companyLine.id,
          duration: lineSpec.duration ?? null,
          distance: lineSpec.distance ?? null,
          realDistance: lineSpec.realDistance ?? null
        }
      }
    )

    const newCompanyLineSpecs = values.companyLineSpecs.map((lineSpec: TSFixMe): TSFixMe => {
      return {
        companyLineID: lineSpec.companyLine.id,
        distance: round(parseFloat(lineSpec.distance) * 1000),
        realDistance: isNumber(lineSpec.realDistance) ? round(parseFloat(lineSpec.realDistance) * 1000) : null,
        duration: parseInt(lineSpec.duration)
      }
    })

    const companyLineSpecsWithDifference = newCompanyLineSpecs.filter((newLineSpec: TSFixMe) => {
      const oldLineSpec = oldCompanyLineSpecs.find(
        (oldLineSpec: TSFixMe) => oldLineSpec.companyLineID === newLineSpec.companyLineID
      )
      return !isEmpty(getDifference(newLineSpec, oldLineSpec))
    })

    if (!isEmpty(companyLineSpecsWithDifference)) {
      await editSegment({
        variables: {
          segmentId: queryData.segment.id,
          companyLineSpecs: companyLineSpecsWithDifference
        }
      })
    }
    navigate('/segments')
  }

  if (networkStatus === NetworkStatus.refetch || queryLoading) {
    content = (
      <Card.Content noHeader noFooter>
        <EmptyState.Container>
          <Loader />
        </EmptyState.Container>
      </Card.Content>
    )
  } else if (queryError) {
    content = (
      <Card.Content noHeader noFooter>
        <EmptyState.Container>
          <EmptyState.Description>
            <FormattedMessage
              id="segments.edit.error.description"
              defaultMessage="There was an error while loading your segment"
            />
          </EmptyState.Description>
          <EmptyState.Action>
            <Button onClick={(): TSFixMe => refetch()} kind="primary">
              <FormattedMessage id="segments.edit.error.action" defaultMessage="Reload your segment" />
            </Button>
          </EmptyState.Action>
        </EmptyState.Container>
      </Card.Content>
    )
  } else {
    content = <NewSegment onSubmit={handleSubmit} segment={queryData.segment} loading={loading} error={error} />
  }

  return (
    <Content hasMaxWidth>
      <ContentHeader>
        <Heading.H3>
          <FormattedMessage id="segments.edit.title" defaultMessage="Edit segment" />
        </Heading.H3>
      </ContentHeader>
      <section>
        <Card.Container>{content}</Card.Container>
      </section>
    </Content>
  )
}

export default withLastUrl(EditSegmentView)
